import { useEffect, useState } from 'react';
import { getAllFormTokenMetaRecords } from '../../../utils/formTokensStorage/getAllFormTokenMetaRecords';
import { writeFormTokenMetaRecords } from '../../../utils/formTokensStorage/writeFormTokenMetaRecords';
import { rehydrateFormTokensMeta } from '../../../utils/rehydrateFormTokensMeta';
import { getPersistedFormTokenChain } from '../../../utils/getPersistedFormTokenChain';
import {
  FormTokensHydration,
  getInitialFormTokensHydration,
} from '../utils/getInitialFormTokensHydration';

// Loads the token meta records from IndexedDB and reattaches them to the
// persisted chain; done synchronously when there is nothing to load
export const useFormTokensHydration = (
  localStorageKey: string
): FormTokensHydration => {
  const [hydration, setHydration] = useState<FormTokensHydration>(() =>
    getInitialFormTokensHydration(localStorageKey)
  );

  useEffect(() => {
    if (hydration.done) {
      return;
    }
    let cancelled = false;
    const persistedChain = getPersistedFormTokenChain(localStorageKey);
    if (!persistedChain) {
      setHydration({ done: true });
      return;
    }
    const hydrate = async () => {
      // failed open is not cached, so one retry gets a fresh connection attempt
      let records = await getAllFormTokenMetaRecords(localStorageKey);
      if (records === null && !cancelled) {
        records = await getAllFormTokenMetaRecords(localStorageKey);
      }
      if (cancelled) {
        return;
      }
      if (records === null) {
        console.error(
          'IndexedDB read failed - form token meta could not be loaded'
        );
        records = [];
      }
      setHydration({
        done: true,
        formTokens: rehydrateFormTokensMeta(records, persistedChain),
      });
      // records no longer referenced by any node are leftovers of a crash
      const referencedIds = new Set(
        Object.values(persistedChain.nodes)
          .map((node) => node.meta)
          .filter((meta): meta is string => typeof meta === 'string')
      );
      const orphanIds = records
        .map((record) => record.id)
        .filter((recordId) => !referencedIds.has(recordId));
      if (orphanIds.length > 0) {
        void writeFormTokenMetaRecords(localStorageKey, [], orphanIds);
      }
    };
    void hydrate();
    return () => {
      cancelled = true;
    };
  }, []);

  return hydration;
};
